import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, Plus, ArrowLeft, Send } from "lucide-react";
import VerifiedBadge from "@/components/app/VerifiedBadge";
import { COUNTRIES } from "@/lib/i18n";
import { toast } from "sonner";

function GroupCard({ g, onJoin, onOpen }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-hover" data-testid={`group-${g.group_id}`}>
      <div className="h-20 relative" style={{ background: `linear-gradient(135deg, ${g.cover_color}, #2A4365)` }}>
        <div className="absolute -bottom-6 left-5 w-12 h-12 rounded-xl border-4 border-white flex items-center justify-center font-display font-extrabold text-white" style={{ background: g.cover_color }}>
          {g.name.charAt(0).toUpperCase()}
        </div>
      </div>
      <div className="px-5 pt-8 pb-5">
        <div className="font-display font-extrabold text-deep">{g.name}</div>
        <div className="text-xs text-slate-500 mt-0.5">{g.specialty || "Communauté transverse"} · {g.members_count} membres</div>
        <p className="mt-2 text-sm text-slate-600 line-clamp-2">{g.description}</p>
        <div className="mt-4 flex gap-2">
          <Button size="sm" onClick={() => onOpen(g)} className="btn-primary" data-testid={`open-grp-${g.group_id}`}>Ouvrir</Button>
          {!g.joined && <Button size="sm" variant="outline" onClick={() => onJoin(g.group_id)} className="border-[#FF7A5C] text-[#FF7A5C] hover:bg-[#FF7A5C] hover:text-white" data-testid={`join-${g.group_id}`}>Rejoindre</Button>}
          {g.joined && <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold flex items-center">Membre ✓</span>}
        </div>
      </div>
    </div>
  );
}

export default function Groups() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [country, setCountry] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ name: "", specialty: "", description: "", country: user?.country || "" });
  const [activeGroup, setActiveGroup] = useState(null);
  const [groupPosts, setGroupPosts] = useState([]);
  const [postText, setPostText] = useState("");

  const load = async () => {
    try {
      const params = country ? { country } : {};
      const { data } = await api.get("/groups", { params });
      setGroups(data);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [country]);

  const join = async (gid) => {
    try { await api.post(`/groups/${gid}/join`); toast.success("Vous avez rejoint le groupe"); load(); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };

  const openGroup = async (g) => {
    try {
      const { data: g2 } = await api.get(`/groups/${g.group_id}`);
      setActiveGroup(g2);
      if (g2.joined) {
        const { data: ps } = await api.get(`/groups/${g.group_id}/posts`);
        setGroupPosts(ps);
      } else {
        setGroupPosts([]);
      }
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };

  const joinThenOpen = async (gid) => {
    await join(gid);
    const { data } = await api.get(`/groups/${gid}`);
    setActiveGroup(data);
    const { data: ps } = await api.get(`/groups/${gid}/posts`);
    setGroupPosts(ps);
  };

  const leaveGroup = async () => {
    if (!activeGroup) return;
    try {
      await api.delete(`/groups/${activeGroup.group_id}/leave`);
      toast.success("Vous avez quitté le groupe");
      setActiveGroup(null);
      load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };

  const createPost = async (e) => {
    e.preventDefault();
    if (!postText.trim() || !activeGroup) return;
    try {
      await api.post(`/groups/${activeGroup.group_id}/posts`, { content: postText });
      setPostText("");
      const { data: ps } = await api.get(`/groups/${activeGroup.group_id}/posts`);
      setGroupPosts(ps);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/groups", form);
      toast.success("Groupe créé");
      setOpenCreate(false);
      setForm({ name: "", specialty: "", description: "", country: user?.country || "" });
      load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };

  const canCreate = ["doctor", "pharmacy"].includes(user?.role);

  if (activeGroup) {
    return (
      <div className="p-6 lg:p-10 max-w-3xl mx-auto">
        <Button variant="ghost" onClick={() => setActiveGroup(null)} className="mb-4 -ms-2" data-testid="grp-back-btn"><ArrowLeft className="h-4 w-4 me-2" />Tous les groupes</Button>
        <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white">
          <div className="h-28" style={{ background: `linear-gradient(135deg, ${activeGroup.cover_color}, #2A4365)` }} />
          <div className="px-6 py-5">
            <h1 className="font-display text-2xl font-extrabold text-deep">{activeGroup.name}</h1>
            <div className="text-sm text-slate-500 mt-1">{activeGroup.specialty || "Communauté transverse"} · {activeGroup.members_count} membres</div>
            <p className="mt-3 text-slate-700">{activeGroup.description}</p>
            <div className="mt-4 flex items-center gap-2">
              {activeGroup.joined ? (
                <Button variant="outline" onClick={leaveGroup} className="border-slate-300" data-testid="leave-grp-btn">Quitter le groupe</Button>
              ) : (
                <Button onClick={() => joinThenOpen(activeGroup.group_id)} className="btn-coral" data-testid="join-grp-detail-btn">Rejoindre</Button>
              )}
            </div>
            {activeGroup.members_preview?.length > 0 && (
              <div className="mt-4 flex -space-x-2">
                {activeGroup.members_preview.slice(0, 6).map((m) => (
                  <div key={m.user_id} className="w-8 h-8 rounded-full bg-gradient-to-br from-[#618DB8] to-[#2A4365] text-white flex items-center justify-center text-xs font-bold border-2 border-white" title={m.name}>
                    {(m.name || "?").replace(/^Dr\s+/, "").charAt(0)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {activeGroup.joined ? (
          <>
            <form onSubmit={createPost} className="mt-6 bg-white border border-slate-200 rounded-2xl p-4">
              <Textarea value={postText} onChange={(e) => setPostText(e.target.value)} placeholder={`Publier dans ${activeGroup.name}…`} rows={2} className="border-0 shadow-none focus-visible:ring-0 p-0" data-testid="grp-compose-input" />
              <div className="flex justify-end mt-2">
                <Button type="submit" disabled={!postText.trim()} className="btn-coral rounded-full" data-testid="grp-publish-btn"><Send className="h-3.5 w-3.5 me-1" />Publier</Button>
              </div>
            </form>
            <div className="mt-6 space-y-3">
              {groupPosts.length === 0 ? <p className="text-sm text-slate-500 text-center py-8">Aucune publication dans ce groupe.</p> :
                groupPosts.map((p) => (
                  <article key={p.post_id} className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#618DB8] to-[#2A4365] text-white flex items-center justify-center text-xs font-bold">{(p.author?.name || "?").charAt(0)}</div>
                      <div className="text-sm font-bold flex items-center">{p.author?.name}{p.author?.blue_check && <VerifiedBadge size={11} />}</div>
                      <div className="text-xs text-slate-400 ms-auto">{new Date(p.created_at).toLocaleString("fr-FR")}</div>
                    </div>
                    <div className="mt-2 whitespace-pre-wrap text-slate-800">{p.content}</div>
                  </article>
                ))}
            </div>
          </>
        ) : (
          <div className="mt-6 text-center text-slate-500 text-sm">Rejoignez le groupe pour voir les publications.</div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-deep">Groupes & Communautés</h1>
          <p className="text-sm text-slate-500 mt-1">Échangez par spécialité ou par sujet avec votre communauté Doctobook.</p>
        </div>
        <div className="flex gap-2">
          <Select value={country || "ALL"} onValueChange={(v) => setCountry(v === "ALL" ? "" : v)}>
            <SelectTrigger className="w-44 h-10" data-testid="grp-country-select"><SelectValue placeholder="Tous pays" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">🌍 Tous pays</SelectItem>
              {COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.flag} {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {canCreate && (
            <Dialog open={openCreate} onOpenChange={setOpenCreate}>
              <DialogTrigger asChild>
                <Button className="btn-coral" data-testid="create-grp-btn"><Plus className="h-4 w-4 me-1" />Créer un groupe</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nouveau groupe</DialogTitle></DialogHeader>
                <form onSubmit={create} className="space-y-3 mt-2">
                  <div><Label>Nom du groupe</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="grp-name-input" /></div>
                  <div><Label>Spécialité (optionnel)</Label><Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} placeholder="Ex: Cardiologie" /></div>
                  <div><Label>Pays (optionnel)</Label>
                    <Select value={form.country || "ALL"} onValueChange={(v) => setForm({ ...form, country: v === "ALL" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="International" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">🌍 International</SelectItem>
                        {COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.flag} {c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Description</Label><Textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="grp-desc-input" /></div>
                  <Button type="submit" className="w-full btn-primary" data-testid="grp-submit-btn">Créer</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-300 rounded-xl text-slate-500">
          <Users className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          Aucun groupe dans votre sélection.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((g) => <GroupCard key={g.group_id} g={g} onJoin={join} onOpen={openGroup} />)}
        </div>
      )}
    </div>
  );
}
